// ════════════════════════════════════════════════════════════════════════
// Shared "Process Payment / Mark as Paid" dialog.
//
// Used by Table Orders and Create Order so the SAME settle flow runs from both
// places. Supports MULTIPLE payments in one go (e.g. ₹100 Cash + ₹100 UPI) and
// defaults the amount to the order's PENDING amount. Posts to /Home/SavePayment
// (one ledger row per leg) which flips the order to Paid once fully settled.
// ════════════════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { toast } from 'react-toastify';
import config from '../../config';
import { requireUserId } from './userSession';

// UI label → backend ledger mode (matches MarkAsPaid/SavePayment).
const METHOD_MAP = { Cash: 'cash', Card: 'card', UPI: 'online' };
const METHODS = ['Cash', 'Card', 'UPI'];

const emptyLeg = (amount = '') => ({
  method: 'Cash',
  amount: amount === '' ? '' : String(amount),
  referenceNo: '',
  cardType: '',
  cardNumber: '',
});

export default function ProcessPaymentDialog({
  open,
  onClose,
  orderId,
  orderLabel,
  pendingAmount = 0,
  currency = '₹',
  onPaid,
}) {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const [legs, setLegs] = useState([emptyLeg()]);
  const [saving, setSaving] = useState(false);

  // On open, pre-fill a single leg with the full pending amount.
  useEffect(() => {
    if (open) {
      setLegs([emptyLeg(Number(pendingAmount || 0).toFixed(2))]);
      setSaving(false);
    }
  }, [open, pendingAmount]);

  const updateLeg = (i, patch) =>
    setLegs((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const addLeg = () => setLegs((prev) => [...prev, emptyLeg()]);
  const removeLeg = (i) =>
    setLegs((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const totalEntered = legs.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const remaining = Number((Number(pendingAmount || 0) - totalEntered).toFixed(2));

  const handleConfirm = async () => {
    if (!orderId) {
      toast.error('Order not found.');
      return;
    }
    const validLegs = legs.filter((l) => Number(l.amount) > 0);
    if (validLegs.length === 0) {
      toast.error('Enter at least one payment amount.');
      return;
    }
    for (const l of validLegs) {
      if (l.method === 'UPI' && !l.referenceNo.trim()) {
        toast.error('Enter Reference / Transaction No. for UPI.');
        return;
      }
      if (l.method === 'Card' && (!l.cardType.trim() || !l.cardNumber.trim())) {
        toast.error('Enter Card Type and Card Number.');
        return;
      }
    }

    const uid = requireUserId();
    if (!uid) return;

    setSaving(true);
    try {
      const payload = {
        OrderId: orderId,
        CompanyId: company?.id ?? null,
        Userid: uid,
        // SplitDetails drives the save — the backend writes one CR row per leg,
        // so this single payload covers both a single tender and a split.
        PaymentMethod: validLegs.length > 1 ? 'split' : METHOD_MAP[validLegs[0].method],
        PaidAmount: totalEntered,
        ReferenceNo: validLegs[0].method === 'UPI' ? validLegs[0].referenceNo : '',
        CardType: validLegs[0].method === 'Card' ? validLegs[0].cardType : '',
        CardNumber: validLegs[0].method === 'Card' ? validLegs[0].cardNumber : '',
        SplitDetails: validLegs.map((l) => ({
          Method: METHOD_MAP[l.method] || 'cash',
          Amount: Number(l.amount) || 0,
          ReferenceNo: l.method === 'UPI' ? l.referenceNo || '' : '',
          CardType: l.method === 'Card' ? l.cardType || '' : '',
          CardNumber: l.method === 'Card' ? l.cardNumber || '' : '',
        })),
      };

      const res = await fetch(`${config.API_BASE_URL}/Home/SavePayment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        toast.error('Payment failed — not recorded. Please retry.');
        return;
      }
      toast.success('Payment recorded.');
      onPaid && onPaid();
      onClose && onClose();
    } catch (err) {
      console.error('ProcessPayment error:', err);
      toast.error('Failed to process payment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1.2, fontWeight: 700 }}>
        <ReceiptIcon color='success' />
        Process Payment{orderLabel ? ` — ${orderLabel}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {/* Pending amount banner */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: '#f0fdf4',
            border: '1px solid #bbf7d0',
          }}>
          <Typography sx={{ fontWeight: 600, color: '#166534' }}>
            Pending Amount
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#15803d' }}>
            {currency} {Number(pendingAmount || 0).toFixed(2)}
          </Typography>
        </Box>

        {/* Payment legs */}
        {legs.map((leg, i) => (
          <Box
            key={i}
            sx={{
              p: 1.5,
              mb: 1.5,
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              bgcolor: '#fafbff',
            }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <FormControl size='small' sx={{ minWidth: 120 }}>
                <InputLabel id={`pm-${i}`}>Method</InputLabel>
                <Select
                  labelId={`pm-${i}`}
                  label='Method'
                  value={leg.method}
                  onChange={(e) => updateLeg(i, { method: e.target.value })}>
                  {METHODS.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m === 'UPI' ? 'UPI / Online' : m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size='small'
                type='number'
                label='Amount'
                value={leg.amount}
                onChange={(e) => updateLeg(i, { amount: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{currency}</InputAdornment>
                  ),
                }}
                sx={{ flex: 1 }}
              />
              {legs.length > 1 && (
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => removeLeg(i)}
                  aria-label='Remove payment'>
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Box>

            {leg.method === 'UPI' && (
              <TextField
                size='small'
                fullWidth
                label='Reference / Transaction No.'
                value={leg.referenceNo}
                onChange={(e) => updateLeg(i, { referenceNo: e.target.value })}
                sx={{ mt: 1.5 }}
              />
            )}
            {leg.method === 'Card' && (
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5 }}>
                <TextField
                  size='small'
                  label='Card Type'
                  placeholder='Visa / RuPay'
                  value={leg.cardType}
                  onChange={(e) => updateLeg(i, { cardType: e.target.value })}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size='small'
                  label='Card Number'
                  placeholder='XXXX'
                  value={leg.cardNumber}
                  onChange={(e) => updateLeg(i, { cardNumber: e.target.value })}
                  sx={{ flex: 1 }}
                />
              </Box>
            )}
          </Box>
        ))}

        <Button
          size='small'
          startIcon={<AddIcon />}
          onClick={addLeg}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          Add another payment
        </Button>

        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ color: '#64748b' }}>Total entered</Typography>
          <Typography sx={{ fontWeight: 700 }}>
            {currency} {totalEntered.toFixed(2)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography sx={{ color: '#64748b' }}>
            {remaining > 0 ? 'Still pending' : remaining < 0 ? 'Overpaid' : 'Settled'}
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              color: remaining > 0 ? '#ea580c' : remaining < 0 ? '#dc2626' : '#16a34a',
            }}>
            {currency} {Math.abs(remaining).toFixed(2)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color='inherit' sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant='contained'
          color='success'
          disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}>
          {saving ? 'Processing...' : 'Confirm & Mark as Paid'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
